import React, { useState } from 'react';
import { Table, Button, Input, InputNumber, DatePicker, Popconfirm, Form, Space } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
}) => {
    let inputNode;
    if (inputType === 'number') {
        inputNode = <InputNumber style={{ width: '100%' }} />;
    } else if (inputType === 'date') {
        inputNode = <DatePicker style={{ width: '100%' }} />;
    } else {
        inputNode = <Input />;
    }

    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{ margin: 0 }}
                    rules={[
                        {
                            required: ['c_code', 'ind_code', 'd_year'].includes(dataIndex),
                            message: `Please Input ${title}!`,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const EditableDataTable = ({ data, loading, pagination, onTableChange, onSave, onDelete, onAdd }) => {
    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    const isEditing = (record) => record.key === editingKey;

    const edit = (record) => {
        form.setFieldsValue({
            c_code: record.c_code,
            c_name: record.c_name,
            u_code: record.u_code,
            d_date: record.d_date ? (dayjs.isDayjs(record.d_date) ? record.d_date : dayjs(record.d_date)) : null,
            ind_code: record.ind_code,
            d_year: record.d_year,
            d_value: record.d_value,
            d_str_value: record.d_str_value,
        });
        setEditingKey(record.key);
    };

    const cancel = () => {
        setEditingKey('');
        setIsAdding(false);
    };

    const save = async (key) => {
        try {
            const row = await form.validateFields();
            
            if (row.d_date && dayjs.isDayjs(row.d_date)) {
                row.d_date = row.d_date.format('YYYY-MM-DD');
            }

            if (isAdding) {
                await onAdd(row);
                setIsAdding(false);
            } else {
                await onSave(row);
            }
            setEditingKey('');
        } catch (errInfo) {
            console.log('Validate Failed:', errInfo);
        }
    };

    const handleDelete = (record) => {
        onDelete(record);
    };

    const handleAdd = () => {
        const newData = {
            key: 'new',
            c_code: null,
            c_name: '',
            u_code: 0,
            d_date: dayjs(),
            ind_code: null,
            d_year: null,
            d_value: null,
            d_str_value: '',
        };
        setIsAdding(true);
        setEditingKey('new');
        form.setFieldsValue(newData);
    };

    const columns = [
        {
            title: 'Country Code',
            dataIndex: 'c_code',
            key: 'c_code',
            width: 120,
            editable: true,
            inputType: 'number',
        },
        {
            title: 'Date',
            dataIndex: 'd_date',
            key: 'd_date',
            width: 120,
            editable: true,
            inputType: 'date',
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Indicator Code',
            dataIndex: 'ind_code',
            key: 'ind_code',
            width: 130,
            editable: true,
            inputType: 'number',
        },
        {
            title: 'Year',
            dataIndex: 'd_year',
            key: 'd_year',
            width: 100,
            editable: true,
            inputType: 'number',
        },
        {
            title: 'Value',
            dataIndex: 'd_value',
            key: 'd_value',
            width: 120,
            editable: true,
            inputType: 'number',
        },
        {
            title: 'String Value',
            dataIndex: 'd_str_value',
            key: 'd_str_value',
            width: 150,
            editable: true,
            inputType: 'text',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <Space>
                        <Button
                            type="link"
                            icon={<SaveOutlined />}
                            onClick={() => save(record.key)}
                        />
                        <Button
                            type="link"
                            icon={<CloseOutlined />}
                            onClick={cancel}
                        />
                    </Space>
                ) : (
                    <Space>
                        <Button
                            type="link"
                            disabled={editingKey !== ''}
                            icon={<EditOutlined />}
                            onClick={() => edit(record)}
                        />
                        <Popconfirm
                            title="Are you sure to delete this record?"
                            onConfirm={() => handleDelete(record)}
                            disabled={editingKey !== ''}
                        >
                            <Button
                                type="link"
                                danger
                                disabled={editingKey !== ''}
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: col.inputType,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    const tableData = isAdding
        ? [
              {
                  key: 'new',
                  c_code: null,
                  c_name: '',
                  u_code: 0,
                  d_date: dayjs(),
                  ind_code: null,
                  d_year: null,
                  d_value: null,
                  d_str_value: '',
              },
              ...data.map((item, index) => ({ ...item, key: `${item.c_code}-${item.ind_code}-${item.d_year}` })),
          ]
        : data.map((item, index) => ({ ...item, key: `${item.c_code}-${item.ind_code}-${item.d_year}` }));

    return (
        <Form form={form} component={false}>
            <Table
                components={{
                    body: {
                        cell: EditableCell,
                    },
                }}
                bordered
                dataSource={tableData}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={{
                    ...pagination,
                    onChange: (page, pageSize) => {
                        if (!isAdding && editingKey === '') {
                            onTableChange({ current: page, pageSize });
                        }
                    },
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                }}
                loading={loading}
                scroll={{ x: 1300 }}
            />
        </Form>
    );
};

export default EditableDataTable;
